import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, Star, Shield,
  ArrowRight, MapPin, CreditCard, User,
  Heart, Sparkles, MessageCircle, Handshake, Bot
} from 'lucide-react';
import { siteConfig } from '../data/mock';
import { TarifsTable, ZonesBlock, PrestationsGrid, CTABox, MiniFAQ, AvoirAussi } from '../components/seo/SEOComponents';
import SolutionsSelonSituation from '../components/seo/SolutionsSelonSituation';
import { LocalBusinessSchema, OrganizationSchema, WebsiteSchema } from '../components/SchemaMarkup';
import { Helmet } from 'react-helmet-async';
import LocationFinder from '../components/LocationFinder';
import AvanceImmediatePromo from '../components/AvanceImmediatePromo';
import HowItWorks from '../components/HowItWorks';
import Testimonials from '../components/Testimonials';
import BeforeAfter from '../components/BeforeAfter';
import FreshnessIndicator from '../components/FreshnessIndicator';
import SiteActivityShowcase from '../components/SiteActivityShowcase';
import TrustStrip from '../components/TrustStrip';
import UpcomingTowns from '../components/UpcomingTowns';
import LawnSeasonBanner from '../components/lawn/LawnSeasonBanner';
import BusinessFacts from '../components/seo/BusinessFacts';

// Why-choose reasons (consolidated trust block)
const WHY_CHOOSE = [
  { icon: Sparkles, title: "Travail propre jusqu'aux finitions", text: "Coupe-bordures, soufflage, ramassage : je soigne chaque détail, pas seulement l'essentiel." },
  { icon: MessageCircle, title: 'Communication simple', text: "Un prix clair avant l'intervention, des échanges faciles par téléphone ou message, une réponse sous 24h." },
  { icon: Heart, title: 'Petits gestes utiles', text: 'Un coup de main raisonnable quand cela reste simple, sans risque et dans le cadre du service à la personne.' },
  { icon: Handshake, title: 'Relation de confiance', text: 'Le même interlocuteur à chaque passage, qui respecte votre maison, vos accès et vos habitudes.' },
  { icon: CreditCard, title: 'Prix clairs TTC', text: "Tarifs affichés TTC, avec le prix « ≈ après crédit d'impôt 50 % ». Service à la Personne déclaré." },
  { icon: MapPin, title: 'Service local autour de Coëx', text: "Basé à Coëx, je connais bien le climat et les sols vendéens et j'interviens dans un rayon de 30 km." },
];

// Engagements (from the Charte) — folded into the trust block
const CHARTE = [
  "Arriver avec respect et prévenir en cas d'imprévu",
  "Donner un prix clair avant intervention",
  "Travailler proprement, avec finitions",
  "Respecter votre maison, vos accès et vos habitudes",
  "Conseiller honnêtement, même si cela me fait gagner moins",
  "Rendre de petits services quand cela reste simple, raisonnable et dans le cadre SAP",
  "Refuser proprement ce qui n'est pas autorisé ou pas sécurisé",
];

const Home = () => {
  const { contact, serviceArea, pricing } = siteConfig;

  // FAQ for homepage
  const homeFAQ = [
    {
      q: "Comment fonctionne le crédit d'impôt de 50% ?",
      a: "En tant que service à la personne déclaré (SAP), mes prestations vous donnent droit à un crédit d'impôt de 50% sur votre déclaration de revenus. Exemple : une facture de 200€ TTC ne vous coûte que 100€ après impôt. Je vous fournis l'attestation fiscale annuelle."
    },
    {
      q: "Dans quelles communes intervenez-vous ?",
      a: `J'interviens dans un rayon de 30 km autour de Coëx (85220), couvrant 31 communes de Vendée : ${serviceArea.cities.slice(0, 8).join(', ')}... et bien d'autres.`
    },
    {
      q: "Comment demander un devis ?",
      a: "Envoyez-moi quelques photos de votre jardin par WhatsApp ou via le formulaire de devis, avec les dimensions approximatives (surface pelouse, longueur haies) et votre commune. Je vous réponds sous 24h avec un devis clair et détaillé."
    },
    {
      q: "Quels sont vos tarifs ?",
      a: `Tonte : ${pricing.tonte.price} (≈ ${pricing.tonte.priceAfterCredit} après crédit). Haies : à partir de 5€/m. Débroussaillage : ${pricing.debroussaillage.price}. Tous les tarifs sont TTC et éligibles au crédit d'impôt 50%.`
    },
    {
      q: "Comment se passe le paiement ?",
      a: "Paiement par virement bancaire ou chèque après intervention. L'Avance immédiate de l'Urssaf est possible sous réserve d'éligibilité et d'activation : si vous êtes éligible, vous ne réglez que le reste à charge (≈ 50 %). Facture conforme SAP fournie."
    },
    {
      q: "Que faire des déchets verts ?",
      a: `Je peux évacuer vos déchets verts vers la déchetterie pour ${pricing.dechets.price} par trajet (environ 1 000 litres). Sinon, vous pouvez garder les déchets pour votre compost ou les déposer vous-même.`
    },
    {
      q: "Quels sont vos horaires de travail ?",
      a: "Je travaille les lundis, mardis, jeudis et vendredis, de 11h à 17h. Je suis fermé le mercredi, le samedi et le dimanche. Le matin, j'entretiens une résidence privée à Brétignolles-sur-Mer."
    },
    {
      q: "Proposez-vous un service de vigilance pour les résidences secondaires ?",
      a: `Oui ! Pour ${pricing.vigilance.price} par passage (≈ ${pricing.vigilance.priceAfterCredit} après crédit), je vérifie votre propriété, relève le courrier, arrose si besoin, et vous envoie des photos. Idéal si vous habitez loin.`
    },
    {
      q: "Est-ce que je peux demander un petit service pendant l'intervention ?",
      a: "Oui, si cela reste simple, raisonnable, sans risque et dans le cadre du service à la personne. Par exemple : déplacer un petit pot, couper une petite branche gênante accessible, souffler une allée ou vérifier un détail extérieur. Si cela devient une vraie prestation, je vous le dirai clairement avant."
    },
    {
      q: "Travaillez-vous plutôt ponctuellement ou en suivi régulier ?",
      a: "Les deux. Certains clients m'appellent pour une intervention ponctuelle, d'autres préfèrent un suivi régulier afin de garder un jardin propre toute l'année."
    },
    {
      q: "Quel matériel utilisez-vous ?",
      a: "L'électrique d'abord : je roule en camionnette électrique et j'utilise des outils électriques pour environ 50 % du travail (taille de haies, soufflage, finitions, petits travaux), pour un environnement calme et propre dans votre quartier. Pour la tonte de pelouse — et la finition nette « signature Le Rosebif » — ainsi que le débroussaillage lourd, j'utilise du matériel thermique professionnel, là où la puissance maximale garantit un résultat propre et régulier."
    },
    {
      q: "Que refusez-vous de faire ?",
      a: "Je reste dans le cadre du service à la personne : jardinage courant, vigilance résidence et petit bricolage simple limité à 2 h. Je ne fais pas plomberie, électricité, chauffage/gaz, gros travaux, élagage à risque, débarras, déménagement ou produits phytosanitaires interdits."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* SEO */}
      <Helmet>
        <title>Jardinier à domicile Coëx (85) | Le Rosebif – SAP 50 %</title>
        <meta name="google-site-verification" content="2j-4IuLSab_4vy6TbCv2BwxbNO5Jk6s6o6S_NLPMvl4" />
        <meta name="description" content="Jardinier à domicile à Coëx et en Vendée. Tonte, haies, débroussaillage : tarifs TTC, devis express. ≈ moitié prix après crédit d'impôt 50 %." />
        <link rel="canonical" href="https://lerosebif.fr/" />
        <meta property="og:title" content="Jardinier à domicile Coëx — Jardinage à domicile Vendée | Le Rosebif" />
        <meta property="og:description" content="Tonte de pelouse, taille de haies, débroussaillage à Coëx. Crédit d'impôt jardinage 50%. Devis gratuit." />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="jardinier à domicile Coëx, jardinage à domicile Vendée, tonte de pelouse Coëx, taille de haies Coëx, débroussaillage Coëx, crédit d'impôt jardinage 50%" />
      </Helmet>
      <LocalBusinessSchema />
      <OrganizationSchema />
      <WebsiteSchema />

      <LawnSeasonBanner />

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-emerald-50 via-white to-lime-50 py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* SAP Badge */}
            <div className="inline-flex items-center gap-2 bg-lime-400 text-emerald-900 px-4 py-2 rounded-full text-sm font-bold mb-6 shadow-sm">
              <Shield size={16} />
              Service à la Personne déclaré — Crédit d'impôt 50%
            </div>

            {/* Live freshness pill */}
            <div className="mb-6">
              <FreshnessIndicator variant="pill" />
            </div>
            
            {/* H1 - Single H1 per page */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Jardinier à domicile à Coëx
              <span className="block text-emerald-700 mt-2">Tonte de pelouse • Taille de haies • Débroussaillage</span>
            </h1>
            
            {/* Intro - 2-4 lines answering who/what/where + SAP */}
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Jardinage à domicile en Vendée : tonte de pelouse, taille de haies, débroussaillage et petit bricolage. 
              Basé à Coëx (85220), j'interviens dans un rayon de 30 km. Grâce au crédit d'impôt jardinage 50%, 
              vous ne payez que la moitié !
            </p>
            
            {/* CTA Buttons - Single devis button, NO WhatsApp */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/devis" data-testid="hero-devis-btn">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all w-full sm:w-auto">
                  Obtenir un devis express
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-full shadow-sm">
                <CheckCircle className="text-emerald-600" size={18} />
                <span>Réponse sous 24h</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-full shadow-sm">
                <Star className="text-amber-500" size={18} />
                <span>5.0 sur Google</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-full shadow-sm">
                <CreditCard className="text-emerald-600" size={18} />
                <span>Crédit d'impôt 50%</span>
              </div>
            </div>

            {/* Avance Immédiate highlight */}
            <div className="mt-6">
              <AvanceImmediatePromo variant="inline" />
            </div>

            {/* Live activity stats strip */}
            <div className="mt-8 pt-6 border-t border-emerald-100">
              <FreshnessIndicator variant="strip" />
            </div>
          </div>
        </div>
      </section>

      {/* H2: Zones desservies - RIGHT AFTER HERO for immediate qualification */}
      <section className="py-12 bg-white" id="zones">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 text-center">
            Êtes-vous dans ma zone d'intervention ?
          </h2>
          <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
            J'interviens dans 31 communes autour de Coëx, dans un rayon de 30 km en Vendée.
          </p>
          <div className="max-w-2xl mx-auto mb-8">
            <LocationFinder />
          </div>
        </div>
      </section>

      {/* Prochainement près de chez vous — upcoming "Prochain passage" towns */}
      <UpcomingTowns />

      {/* H2: Prestations principales */}
      <section className="py-12 lg:py-16 bg-gray-50" id="prestations">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 text-center">
            Prestations principales
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            Entretien complet de votre jardin, toute l'année. Tous les tarifs sont TTC et éligibles au crédit d'impôt 50%.
          </p>
          <PrestationsGrid />
        </div>
      </section>

      {/* Nos Réalisations - Before/After Gallery (moved up: visual proof) */}
      <BeforeAfter maxItems={6} />

      {/* Comment ça se passe - How It Works */}
      <HowItWorks variant="full" />

      {/* H2: Tarifs TTC */}
      <section className="py-12 lg:py-16 bg-white" id="tarifs">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 text-center">
            Tarifs TTC
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            Des prix clairs et honnêtes. Profitez du crédit d'impôt 50% sur toutes les prestations.
          </p>
          <div className="max-w-3xl mx-auto">
            <TarifsTable />
          </div>
          <p className="text-center mt-6">
            <Link to="/tarifs" className="text-emerald-600 hover:text-emerald-700 font-medium">
              Voir tous les détails des tarifs →
            </Link>
          </p>
        </div>
      </section>

      {/* H2: Pourquoi choisir Le Rosebif — consolidated trust block (service humain + charte + raisons) */}
      <section className="py-12 lg:py-16 bg-gray-50" id="pourquoi" data-testid="pourquoi-section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                <Heart size={15} /> Un service humain
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                Pourquoi choisir Le Rosebif
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Je ne viens pas simplement tondre et repartir : un travail propre, une communication simple,
                des conseils honnêtes et de petits gestes utiles quand cela reste raisonnable — vous gardez un jardin
                soigné et une personne de confiance, dans le cadre du service à la personne.
              </p>
            </div>

            {/* 6 raisons */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
              {WHY_CHOOSE.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                    <div className="bg-emerald-100 p-3 rounded-full flex-shrink-0">
                      <Icon className="text-emerald-600" size={22} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* La Charte — engagements (folded in, compact) */}
            <div className="bg-white rounded-2xl border border-emerald-100 p-6 lg:p-8">
              <div className="flex items-center justify-center gap-2.5 mb-5">
                <Handshake className="text-emerald-600" size={22} />
                <h3 className="text-xl font-bold text-gray-900 text-center">La Charte Le Rosebif</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {CHARTE.map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <CheckCircle className="text-emerald-500 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-center text-gray-600 max-w-2xl mx-auto mt-8 mb-5">
              Anglais de naissance, vendéen d'adoption — je mets un point d'honneur à fournir un service fiable et ponctuel.
              Découvrez{' '}
              <Link to="/jardinier-de-confiance-vendee" className="text-emerald-600 hover:text-emerald-700 font-medium">
                l'esprit d'un jardinier de confiance en Vendée
              </Link>.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/devis" data-testid="pourquoi-devis-btn">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-7">
                  Demander un devis express <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/mon-parcours" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold" data-testid="pourquoi-parcours-link">
                <User size={18} /> Découvrir mon parcours →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Robot tondeuse — finitions de jardin */}
      <section className="py-12 lg:py-16 bg-gradient-to-br from-emerald-50 via-white to-lime-50" data-testid="robot-tondeuse-home-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-6 lg:p-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-emerald-100 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="text-emerald-700" size={24} />
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">
                  Vous avez un robot tondeuse ? Je m'occupe des finitions
                </h2>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Un robot tondeuse garde la pelouse courte, mais il ne fait pas toujours les bordures, les angles,
                les pieds de haies ou les contours de terrasse. Le Rosebif propose un passage de finition pour
                garder un jardin propre, net et soigné.
              </p>
              <p className="text-gray-700 mb-2">
                Devis personnalisé selon le jardin, les bordures et la fréquence souhaitée.
              </p>
              <p className="text-sm text-gray-500 italic mb-6">
                Service à la personne – le devis indiquera le montant TTC et l'estimation « ≈ après crédit d'impôt 50 % ».
              </p>
              <Link to="/entretien-pelouse-robot-tondeuse" data-testid="robot-home-cta">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-7">
                  Voir le service robot tondeuse
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions selon votre situation */}
      <section className="py-12 lg:py-16 bg-white" data-testid="home-solutions-section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <SolutionsSelonSituation />
          </div>
        </div>
      </section>

      {/* Avance Immédiate Promo Block */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <AvanceImmediatePromo variant="full" />
        </div>
      </section>

      {/* Site-wide activity showcase — keyword-rich service breakdown */}
      <SiteActivityShowcase />

      {/* Testimonials */}
      <Testimonials variant="full" />

      {/* Live Trust Strip — recurring clients social proof */}
      <TrustStrip variant="full" />

      {/* Mon Parcours Promo Section */}
      <section className="py-12 bg-gradient-to-br from-slate-800 via-slate-900 to-emerald-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-1/3">
              <img 
                src="/photos/hassan-portrait-tonte.webp"
                alt="Mr Ingram, fondateur de Le Rosebif, jardinier à Coëx en Vendée"
                className="w-full h-48 md:h-56 object-cover rounded-xl shadow-lg"
                style={{ objectPosition: '72% 22%' }}
                loading="lazy"
                data-testid="home-founder-photo"
              />
            </div>
            <div className="md:w-2/3 text-center md:text-left">
              <h2 className="text-2xl font-bold mb-3">Qui suis-je ?</h2>
              <p className="text-gray-300 mb-4">
                De Devon à la Vendée, découvrez mon histoire, mes engagements et mon quotidien sur le terrain. 
                Un nom qui fait sourire, mais un service très sérieux.
              </p>
              <Link 
                to="/mon-parcours"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-3 px-6 rounded-xl transition-all"
              >
                Lire mon parcours
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Le Rosebif en bref — factual summary */}
      <BusinessFacts />

      {/* H2: FAQ */}
      <section className="py-12 lg:py-16 bg-gray-50" id="faq">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <MiniFAQ questions={homeFAQ} title="Questions fréquentes" />
            <p className="text-center mt-6">
              <Link to="/faq" className="text-emerald-600 hover:text-emerald-700 font-medium">
                Voir toutes les questions →
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 lg:py-16 bg-white" id="contact">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <CTABox variant="primary" />
          </div>
        </div>
      </section>

      {/* À voir aussi */}
      <section className="py-12 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <AvoirAussi currentPage="/" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
